import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';

// Audit log (admin): who did what and when, with failures easy to find and inspect.

const panel = { background: 'rgba(15,23,42,0.92)', borderRadius: 16, border: '1px solid rgba(56,189,248,0.15)' };
const input = { background: '#ffffff', border: '1.5px solid #94a3b8', borderRadius: 10, padding: '8px 10px', color: '#0f172a', fontSize: 14.5, fontFamily: 'inherit', colorScheme: 'light' };
const opt = { background: '#0f172a' };
const th = { padding: '10px 12px', textAlign: 'left', color: '#cbd5e1', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '9px 12px', color: '#e2e8f0', fontSize: 14.5, verticalAlign: 'top' };
const btn = (color = '#1a5fa4') => ({ background: color, border: `1px solid ${color}`, borderRadius: 8, padding: '8px 12px', color: '#ffffff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });

const todayEAT = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Nairobi' }).format(new Date());
const daysAgo = (n) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Nairobi' }).format(new Date(Date.now() - n * 86400000));
const when = (d) => new Date(d).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'medium' });
const ACTOR_LABEL = { user: 'User', system: 'System', provider: 'Payment provider', anonymous: 'Not signed in' };

const Result = ({ e }) => (
  <span style={{ color: e.success ? '#10b981' : '#f87171', fontWeight: 600, whiteSpace: 'nowrap' }}>
    {e.success ? '✓ OK' : '✕ Failed'}{e.status_code ? <span style={{ color: '#cbd5e1', fontWeight: 400 }}> · {e.status_code}</span> : null}
  </span>
);

function Detail({ id, headers, onClose, onOpen }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`/api/audit/${id}`, { headers }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [id, headers]);

  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const e = data?.entry;
  const row = (k, v) => (v === null || v === undefined || v === '' ? null : (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 14.5 }}>
      <span style={{ color: '#cbd5e1' }}>{k}</span><span style={{ color: '#e2e8f0', wordBreak: 'break-word' }}>{v}</span>
    </div>
  ));

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}>
      <div role="dialog" aria-modal="true" aria-label="Audit entry" onClick={(ev) => ev.stopPropagation()}
        style={{ width: 'min(560px, 100%)', height: '100%', overflowY: 'auto', background: '#0f172a', borderLeft: '1px solid rgba(56,189,248,0.2)', padding: 24, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ color: '#e2e8f0', margin: 0, fontSize: 17 }}>{e ? e.action : 'Loading…'}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: '#e2e8f0', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
        {data?.error && <p style={{ color: '#f87171' }}>Could not load this entry.</p>}
        {e && (
          <>
            {!e.success && (
              <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: '10px 14px', color: '#fca5a5', fontSize: 14.5, marginBottom: 16, wordBreak: 'break-word' }}>
                <b>What went wrong:</b> {e.error || 'No message recorded'}
              </div>
            )}
            {row('When', when(e.created_at))}
            {row('Result', <Result e={e} />)}
            {row('Who', `${e.user_email || ACTOR_LABEL[e.actor_type]}${e.user_role ? ` (${e.user_role})` : ''}`)}
            {row('Site', e.tenant || (e.tenant_id ? `Tenant ${e.tenant_id}` : 'Main site'))}
            {row('Record', e.entity_type ? `${e.entity_type}${e.entity_id ? ` #${e.entity_id}` : ''}` : null)}
            {row('Request', e.method ? `${e.method} ${e.path}` : null)}
            {row('Took', e.duration_ms !== null ? `${e.duration_ms} ms` : null)}
            {row('IP address', e.ip)}
            {row('Browser', e.user_agent)}
            {row('Request ID', e.request_id && <span style={{ fontFamily: 'monospace', fontSize: 13.5 }}>{e.request_id}</span>)}

            <div style={{ color: '#cbd5e1', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, margin: '18px 0 6px' }}>Details sent</div>
            <pre style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 10, padding: 12, color: '#cbd5e1', fontSize: 13.5, overflowX: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {e.details ? JSON.stringify(e.details, null, 2) : '—'}
            </pre>
            <div style={{ color: '#cbd5e1', fontSize: 13.5, marginTop: 6 }}>Passwords, keys and tokens are never stored. They show as [redacted].</div>

            {data.related?.length > 0 && (
              <>
                <div style={{ color: '#cbd5e1', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, margin: '18px 0 6px' }}>
                  Related events ({data.related.length})
                </div>
                {data.related.map((r) => (
                  <button key={r.id} onClick={() => onOpen(r.id)}
                    style={{ display: 'grid', gridTemplateColumns: '130px 1fr auto', gap: 8, width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, padding: '8px 10px', marginBottom: 6, cursor: 'pointer', color: '#cbd5e1', fontSize: 13.5 }}>
                    <span style={{ color: '#cbd5e1' }}>{when(r.created_at)}</span>
                    <span>{r.action}{r.error ? <span style={{ color: '#fca5a5' }}> · {r.error}</span> : ''}</span>
                    <span style={{ color: r.success ? '#10b981' : '#f87171' }}>{r.success ? '✓' : '✕'}</span>
                  </button>
                ))}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function AuditPage() {
  const [filters, setFilters] = useState({ from: daysAgo(6), to: todayEAT(), user_id: '', action: '', outcome: '', actor: '', q: '' });
  const [search, setSearch] = useState('');
  const [facets, setFacets] = useState({ actions: [], users: [], failures24h: 0 });
  const [data, setData] = useState({ entries: [], total: 0 });
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const PAGE = 50;

  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));

  useEffect(() => {
    axios.get('/api/audit/facets', { headers }).then((r) => setFacets(r.data)).catch((e) => {
      if (e.response?.status === 401) { localStorage.clear(); navigate('/login'); }
      if (e.response?.status === 403) navigate('/dashboard');
    });
  }, [headers, navigate]);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/audit', { headers, params: { ...params, limit: PAGE, offset: page * PAGE } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const set = (k, v) => { setPage(0); setFilters((f) => ({ ...f, [k]: v })); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get('/api/audit/export', { headers, params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${filters.from || 'all'}_to_${filters.to || 'now'}.csv`; a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  // Group actions by area for the dropdown: "payment.retry" → Payment
  const actionGroups = facets.actions.reduce((g, a) => {
    const area = a.action.includes('.') ? a.action.split('.')[0] : 'other';
    (g[area] = g[area] || []).push(a);
    return g;
  }, {});
  const pages = Math.ceil(data.total / PAGE);

  return (
    <div style={{ minHeight: '100vh' }}>
      <Navbar user={user} />
      <div className="mw-page" style={{ padding: '32px 24px', maxWidth: 1240, margin: '0 auto' }}>
        <div className="mw-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ color: '#0f172a', fontSize: 26, fontWeight: 700, margin: 0 }}>Audit log</h1>
            <p style={{ color: '#475569', fontSize: 15, margin: '6px 0 0' }}>Every change made in the system, sign-ins, payment callbacks and background jobs</p>
          </div>
          {facets.failures24h > 0 && (
            <button onClick={() => setFilters((f) => ({ ...f, outcome: 'failed', from: daysAgo(1), to: todayEAT() }))} style={btn('#b91c1c')}>
              {facets.failures24h} failure{facets.failures24h === 1 ? '' : 's'} in the last 24 hours
            </button>
          )}
        </div>

        {/* Filters — one row */}
        <div data-tour="audit-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <input type="date" aria-label="From" value={filters.from} max={filters.to || undefined} onChange={(e) => set('from', e.target.value)} style={input} />
          <span style={{ color: '#475569' }}>to</span>
          <input type="date" aria-label="To" value={filters.to} min={filters.from || undefined} onChange={(e) => set('to', e.target.value)} style={input} />
          <select aria-label="Outcome" value={filters.outcome} onChange={(e) => set('outcome', e.target.value)} style={input}>
            <option value="" style={opt}>All results</option>
            <option value="failed" style={opt}>Failures only</option>
            <option value="ok" style={opt}>Successful only</option>
          </select>
          <select aria-label="User" value={filters.user_id} onChange={(e) => set('user_id', e.target.value)} style={input}>
            <option value="" style={opt}>Everyone</option>
            {facets.users.map((u) => <option key={u.id} value={u.id} style={opt}>{u.name}</option>)}
          </select>
          <select aria-label="Who" value={filters.actor} onChange={(e) => set('actor', e.target.value)} style={input}>
            <option value="" style={opt}>Users + system</option>
            {Object.entries(ACTOR_LABEL).map(([k, v]) => <option key={k} value={k} style={opt}>{v}</option>)}
          </select>
          <select aria-label="Action" value={filters.action} onChange={(e) => set('action', e.target.value)} style={{ ...input, maxWidth: 220 }}>
            <option value="" style={opt}>All actions</option>
            {Object.entries(actionGroups).map(([area, list]) => (
              <optgroup key={area} label={area.charAt(0).toUpperCase() + area.slice(1)} style={opt}>
                <option value={`${area}.`} style={opt}>All {area}</option>
                {list.map((a) => <option key={a.action} value={a.action} style={opt}>{a.action} ({a.n})</option>)}
              </optgroup>
            ))}
          </select>
          <form onSubmit={(e) => { e.preventDefault(); set('q', search.trim()); }} style={{ display: 'flex', gap: 6 }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Email, error, meter, IP, request ID…" aria-label="Search" style={{ ...input, width: 230 }} />
            <button type="submit" style={btn()}>Search</button>
          </form>
          <div style={{ flex: 1 }} />
          <button onClick={exportCsv} disabled={exporting || !data.total} style={{ ...btn('#10b981'), opacity: data.total ? 1 : 0.5 }}>{exporting ? 'Exporting…' : '⬇ Export CSV'}</button>
        </div>

        <div style={{ ...panel, overflow: 'hidden', opacity: loading ? 0.6 : 1 }}>
          <div style={{ padding: '12px 16px', color: '#cbd5e1', fontSize: 14.5, borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
            {data.total.toLocaleString()} event{data.total === 1 ? '' : 's'} · click a row for full details
          </div>
          {data.entries.length === 0 ? (
            <p style={{ color: '#cbd5e1', textAlign: 'center', padding: 40, margin: 0 }}>{loading ? 'Loading…' : 'Nothing matches these filters'}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{['When', 'Who', 'Action', 'Record', 'Result', 'Problem', 'Site', 'IP'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={e.id} onClick={() => setOpenId(e.id)} tabIndex={0} onKeyDown={(ev) => ev.key === 'Enter' && setOpenId(e.id)}
                      style={{ borderTop: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', background: e.success ? 'transparent' : 'rgba(248,113,113,0.05)' }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{when(e.created_at)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#cbd5e1' }} title={ACTOR_LABEL[e.actor_type]}>{e.user_email || ACTOR_LABEL[e.actor_type]}</td>
                      <td style={{ ...td, color: '#e2e8f0', fontFamily: 'monospace', fontSize: 13.5, whiteSpace: 'nowrap' }}>{e.action}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{e.entity_type ? `${e.entity_type}${e.entity_id ? ` #${e.entity_id}` : ''}` : '—'}</td>
                      <td style={td}><Result e={e} /></td>
                      <td style={{ ...td, color: '#fca5a5', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.error || ''}>{e.success ? '' : e.error}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{e.tenant || (e.tenant_id ? `#${e.tenant_id}` : '—')}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 13.5 }}>{e.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', padding: 12, color: '#cbd5e1', fontSize: 14.5 }}>
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={btn('#334155')}>‹</button>
              Page {page + 1} of {pages}
              <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} style={btn('#334155')}>›</button>
            </div>
          )}
        </div>
        <p style={{ color: '#cbd5e1', fontSize: 13.5, marginTop: 12 }}>
          Entries are kept for a year (AUDIT_RETENTION_DAYS) and can’t be edited or deleted from here. If someone sees “Something went wrong”,
          the Request ID in their browser’s network tab can be searched here to find exactly what happened.
        </p>
      </div>
      {openId && <Detail id={openId} headers={headers} onClose={() => setOpenId(null)} onOpen={setOpenId} />}
    </div>
  );
}
